import type { DomainIdentity, WhoamiResponse } from './types'

/**
 * Authentication is delegated to an external system; the UI learns who (if anyone)
 * is signed in by asking the content API. `whoami` answers 200 with the profile for
 * a valid session and 403 for an anonymous one.
 *
 * Same-origin on purpose: the shell and the content API share this host, so the
 * request carries the `__Host-` session cookie with no `credentials`/CORS handling
 * — the whole reason the dev server proxies the API onto this origin.
 */
const WHOAMI_URL = '/api/3.0/profile/whoami'

/**
 * What one whoami call learned.
 *
 * The important part is that a 403 is a **successful answer**, not a failure: the
 * body still carries the `domain` block naming the tenant. Older code here treated
 * any non-200 as a rejection and threw the body away with it, which is exactly the
 * information telemetry needs on an anonymous visit — so the status and the parsed
 * body are both surfaced and the caller decides what they mean.
 */
export interface WhoamiResult {
  /** True for the 200 that carries a `profile` — i.e. somebody is signed in. */
  authenticated: boolean;
  /** The HTTP status, or 0 when the request never reached the server. */
  status: number;
  /** The parsed body, on any status. Null only when there was nothing to parse. */
  body: WhoamiResponse | null;
}

/**
 * The single in-flight (then settled) whoami request, shared by every consumer —
 * the same pattern as `loadDomainConfiguration`, and for the same reason: two callers
 * now need this answer at two different moments and there must still be one request.
 */
let request: Promise<WhoamiResult> | undefined

/**
 * Ask who is signed in, once per page load.
 *
 * **Never rejects.** A network failure, an unparseable body and a 500 all resolve to
 * `{ authenticated: false, body: null }`, because every caller's answer to "we could
 * not find out" is the same as its answer to "nobody is signed in" — and because a
 * shared promise that rejects would need a `.catch` at every consumer to avoid an
 * unhandled rejection.
 *
 * Called at shell startup (to publish the tenant to telemetry as early as possible)
 * and again by {@link AuthProvider} on mount; both get the same request.
 */
export function loadWhoami (): Promise<WhoamiResult> {
  if (!request) {
    request = fetch(WHOAMI_URL, { headers: { Accept: 'application/json' } })
      .then(res =>
        // Parse the body whatever the status — the 403's body is where an anonymous
        // visitor's tenant comes from. A body that will not parse is not an error
        // either; it just means we learned only the status.
        res.json()
          .catch(() => null)
          .then((body: WhoamiResponse | null) => ({
            // `profile` is what makes it a session. Keyed on the payload rather than
            // on `res.ok` so a backend that starts answering 200-with-no-profile
            // cannot silently promote an anonymous visitor.
            authenticated: res.ok && !!body?.profile,
            status: res.status,
            body,
          }))
      )
      .catch(() => ({ authenticated: false, status: 0, body: null }))
  }
  return request
}

/**
 * The tenant this deployment serves, once whoami has answered. Null if it never did.
 *
 * Separate from the session on purpose: `domain` describes the *site*, not the
 * viewer, and is present on the anonymous 403 too — so telemetry can be scoped to a
 * tenant without anyone being signed in.
 */
export function loadDomainIdentity (): Promise<DomainIdentity | null> {
  return loadWhoami().then(result => result.body?.domain ?? null)
}
